import { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { NotificationService } from '@setup/notifications/SnackbarUtilsConfigurator/SnackbarUtilsConfigurator';
import { ModalNamesEnum } from '@shared/enums/modalNames.enum';
import { useAppDispatch } from '@shared/hooks/redux';
import { changeCoverPhoto } from '@shared/utils/hubs/changeCoverPhoto';
import modalObserver from '@shared/utils/observers/modalObserver';
import { setLoading } from '@store/Common/commonSlice';
import { deleteMedia } from '@store/Common/commonThunk';

import type { AttachmentsProps } from '../Attachments';
import type { MediaType } from '@shared/models/media.model';

type ParamsType = {} & Pick<
  AttachmentsProps,
  | 'setIsLoadingAttachment'
  | 'isDeleteWithAutoSave'
  | 'isShowActionMenu'
  | 'handleDeleteCoverPhoto'
  | 'showDeletePopupOnDelete'
  | 'attachments'
  | 'handelUpdateAttachment'
  | 'entityType'
  | 'permission'
  | 'cropConfig'
  | 'attachmentType'
  | 'handleAddAttachment'
  | 'isProtectedUrl'
>;

export const useAttachments = (params: ParamsType) => {
  const {
    setIsLoadingAttachment,
    attachments,
    showDeletePopupOnDelete,
    isShowActionMenu,
    handleDeleteCoverPhoto,
    isDeleteWithAutoSave,
    permission,
    entityType,
    attachmentType,
    handelUpdateAttachment,
    handleAddAttachment,
    cropConfig,
    isProtectedUrl,
  } = params;

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const initialMount = useRef(true);
  const [loadingAttachment, setLoadingAttachment] = useState<MediaType[]>([]);
  const [newAttachments, setNewAttachments] = useState<MediaType[]>(attachments);

  useEffect(() => {
    setIsLoadingAttachment?.(!!loadingAttachment.length);
    return () => {
      return setIsLoadingAttachment?.(false);
    };
  }, [loadingAttachment, setIsLoadingAttachment]);

  const handleUpdateLoadingAttachment = (id: string, progress: number) => {
    setLoadingAttachment((prev) =>
      prev.map((item) => {
        if (item.progressId === id) {
          return { ...item, progress };
        }
        return item;
      }),
    );
  };

  const handleDeleteLoadingAttachment = (id: string) => {
    setLoadingAttachment((prev) => prev.filter((item) => item.progressId !== id));
  };

  const handleAddLoadingAttachment = (newLoadingAttachment: MediaType) => {
    setLoadingAttachment((prevState) => {
      return [newLoadingAttachment, ...prevState];
    });
  };

  const handleDeleteAttachment = useCallback(
    (id: number, token: string, attachmentList: MediaType[]) => {
      const deletedMedia: MediaType | undefined = attachmentList.find((item) => item.id === id);
      const deletedMediaIndex = attachmentList.findIndex((item) => item.id === id);
      const newMedia = attachmentList.filter((item) => item.id !== id);
      const { photos } = changeCoverPhoto(newMedia);

      if (isDeleteWithAutoSave) {
        dispatch(setLoading(true));
        dispatch(deleteMedia(token))
          .then(() => {
            setNewAttachments(isShowActionMenu ? photos : newMedia);
            NotificationService.success(
              t(`general.notifications.${attachmentType === 'photo' ? 'photoDelete' : 'fileDelete'}`),
            );

            if (isShowActionMenu) {
              handleDeleteCoverPhoto?.(id, deletedMedia?.is_cover);
            }
            if (isShowActionMenu || showDeletePopupOnDelete) {
              modalObserver.removeModal(ModalNamesEnum.alertModal);
            }
          })
          .catch(() => {
            if (deletedMedia !== undefined) {
              const returnedMedia = [
                ...attachmentList.slice(0, deletedMediaIndex),
                deletedMedia,
                ...attachmentList.slice(deletedMediaIndex + 1),
              ];

              setNewAttachments(returnedMedia);
            }
          })
          .finally(() => dispatch(setLoading(false)));
      } else {
        setNewAttachments(newMedia);
      }
    },
    [
      isDeleteWithAutoSave,
      dispatch,
      setNewAttachments,
      isShowActionMenu,
      t,
      handleDeleteCoverPhoto,
      showDeletePopupOnDelete,
      attachmentType,
    ],
  );
  useEffect(() => {
    if (initialMount.current) {
      initialMount.current = false;
      return;
    }

    handleAddAttachment(newAttachments);
  }, [handleAddAttachment, newAttachments]);

  const onAddAsyncAttachment = (file: MediaType) => {
    setNewAttachments((prevAttachments) => [file, ...prevAttachments]);
  };

  const handleOpenViewModal = (index?: number | undefined) => {
    modalObserver.addModal(ModalNamesEnum.mediaViewer, {
      props: {
        media: attachments,
        activeMedia: index ? index : 0,
        onDelete: handleDeleteAttachment,
        onUpdate: handelUpdateAttachment,
        entityType,
        permission,
        cropConfig,
        isProtectedUrl,
      },
    });
  };

  return {
    onAddAsyncAttachment,
    handleOpenViewModal,
    handleAddLoadingAttachment,
    handleDeleteLoadingAttachment,
    handleUpdateLoadingAttachment,
    loadingAttachment,
    handleDeleteAttachment,
  };
};
